import { CelestialBody } from '../core/world/body'
import { OrbitElements } from '../core/orbit/elements'
import { Evento } from '../core/orbit/prediction'
import { Manovra, deltaVDi } from '../core/orbit/maneuver'
import { OrbitReadouts } from '../core/nav/readouts'
import { distanza, tempo, velocita } from './format'

const NOMI_EVENTO: Record<Evento['tipo'], string> = {
  periapside: 'periapside',
  apoapside: 'apoapside',
  impatto: 'IMPATTO',
  atmosfera: 'ingresso in atmosfera',
  'entra-soi': 'ingresso nella sfera di',
  'esce-soi': 'uscita verso',
}

export interface StatoMappa {
  readonly body: CelestialBody
  /** inclinazione dell'orbita, rad */
  readonly inclinazione: number
  readonly letture: OrbitReadouts
  readonly eventi: Evento[]
  readonly manovra: Manovra | null
  readonly orbitaManovra: OrbitElements | null
  readonly timeWarp: number
  readonly suRotaia: boolean
}

export interface PannelloMappa {
  aggiorna(s: StatoMappa): void
}

export function createPannelloMappa(root: HTMLElement): PannelloMappa {
  root.innerHTML = ''
  root.className = 'mappa-ui'

  const titolo = document.createElement('h1')
  titolo.textContent = 'Mappa orbitale'
  const riferimento = document.createElement('p')
  riferimento.className = 'riferimento'
  const dl = document.createElement('dl')
  const eventi = document.createElement('div')
  eventi.className = 'eventi'
  const manovra = document.createElement('div')
  manovra.className = 'manovra'
  const comandi = document.createElement('p')
  comandi.className = 'comandi-mappa'
  comandi.innerHTML = '<b>N</b> nodo all’apoapside · <b>[</b> <b>]</b> regola Δv · <b>K</b> circolarizza · <b>Canc</b> togli'

  root.append(titolo, riferimento, dl, eventi, manovra, comandi)

  const celle = new Map<string, HTMLElement>()
  for (const [campo, etichetta] of [
    ['apoapside', 'apoapside'], ['periapside', 'periapside'],
    ['periodo', 'periodo'], ['eccentricita', 'eccentricità'],
    ['inclinazione', 'inclinazione'], ['warp', 'tempo'],
  ] as const) {
    const dt = document.createElement('dt')
    dt.textContent = etichetta
    const dd = document.createElement('dd')
    dd.dataset.campo = campo
    dd.textContent = '—'
    dl.append(dt, dd)
    celle.set(campo, dd)
  }

  return {
    aggiorna(s) {
      riferimento.textContent = `riferimento: ${s.body.name}`
      celle.get('apoapside')!.textContent = distanza(s.letture.apoapsis)
      celle.get('periapside')!.textContent = distanza(s.letture.periapsis)
      celle.get('periodo')!.textContent = tempo(s.letture.period)
      celle.get('eccentricita')!.textContent = s.letture.eccentricity.toFixed(4)
      celle.get('inclinazione')!.textContent = `${((s.inclinazione * 180) / Math.PI).toFixed(2)}°`
      celle.get('warp')!.textContent = `×${s.timeWarp}${s.suRotaia ? ' su rotaia' : ''}`

      eventi.innerHTML = ''
      const h = document.createElement('h2')
      h.textContent = 'In arrivo'
      eventi.appendChild(h)
      if (s.eventi.length === 0) {
        const p = document.createElement('p')
        p.className = 'niente'
        p.textContent = 'niente all’orizzonte'
        eventi.appendChild(p)
      }
      for (const e of s.eventi.slice(0, 5)) {
        const riga = document.createElement('div')
        riga.className = `evento ${e.tipo}`
        const nome = document.createElement('span')
        nome.textContent = e.corpo ? `${NOMI_EVENTO[e.tipo]} ${e.corpo}` : NOMI_EVENTO[e.tipo]
        const quando = document.createElement('span')
        quando.className = 'quando'
        quando.textContent = `fra ${tempo(e.quando)}`
        riga.append(nome, quando)
        eventi.appendChild(riga)
      }

      manovra.innerHTML = ''
      if (s.manovra) {
        const h2 = document.createElement('h2')
        h2.textContent = 'Nodo di manovra'
        const dv = document.createElement('p')
        dv.className = 'dv'
        dv.textContent = `Δv ${velocita(deltaVDi(s.manovra))} · fra ${tempo(s.manovra.quando)}`
        manovra.append(h2, dv)
      }
    },
  }
}
